import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useMutation } from "@apollo/client";
import { UPDATE_EXTERNAL_USER_ACCESS } from "../../../../apis/organization-apis/people-apis/externalUserApis";
import ConfirmSnackbar from "../../../../components/Snackbar/ConfirmSnackbar";
import DatePicker from "../../../../components/DatePicker/DatePicker";
import { formatDateForInput } from "../../../../utils/dateUtils";

const EditAccessDateDialog = ({
  open,
  onClose,
  user,
  dateType, 
  currentDate,
  refetch,
}) => {
  const [updateAccess, { loading: updating }] = useMutation(UPDATE_EXTERNAL_USER_ACCESS);
  const [snackbarInfo, setSnackbarInfo] = useState(null);
  const [date, setDate] = useState("");

  useEffect(() => {
    if (open) {
      setDate(formatDateForInput(currentDate));
    } else {
      setDate("");
    }
  }, [currentDate, open]);

  const handleSave = async () => {
    if (!user?.id) return;

    try {
      const input = {
        external_user_id: user.id,
        role_id: user.role_id,
      };

      if (dateType === "access_from") {
        input.access_from = date || null;
      } else if (dateType === "access_till") {
        input.access_till = date || null;
      }

      const result = await updateAccess({
        variables: { input },
      });

      if (result?.data?.updateExternalUserAccess?.success) {
        setSnackbarInfo({
          message: `${dateType === "access_from" ? "Access From" : "Access Till"} Updated Successfully`,
          vertical: "top",
          horizontal: "right",
        });
        await refetch();
        onClose();
      } else {
        setSnackbarInfo({
          message: result?.data?.updateExternalUserAccess?.message || "Failed to update Access Date",
          vertical: "top",
          horizontal: "right",
        });
      }
    } catch (error) {
      console.error("Error updating access date:", error);
      setSnackbarInfo({
        message: error.message || "Failed to update Access Date",
        vertical: "top",
        horizontal: "right",
      });
    }
  };

  const handleClose = () => {
    setDate(formatDateForInput(currentDate));
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            Edit {dateType === "access_from" ? "Access From" : "Access Till"}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: 3, pb: 2 }}>
          <Box sx={{ pt: 2 }}>
            <DatePicker
              fullWidth
              label={dateType === "access_from" ? "Access From Date" : "Access Till Date"}
              value={date}
              onChange={(newDate) => setDate(newDate)}
              disabled={updating}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 2, borderTop: "1px solid #e5e7eb" }}>
          <Button
            onClick={handleClose}
            disabled={updating}
            sx={{
              color: "#6b7280",
              fontWeight: 500,
              "&:hover": {
                backgroundColor: "#f3f4f6",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={updating}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmSnackbar snackbarInfo={snackbarInfo} />
    </>
  );
};

export default EditAccessDateDialog;
